export const DEFAULT_FREE_EVENT = false;
export const DEFAULT_ENABLE_EMISSION = false;
export const DEFAULT_ENABLE_QUESTIONS = false;

const normalizeValue = (value) => String(value || "").trim().toLowerCase();

const parseBoolean = (value, fallback) => {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = normalizeValue(value);
    if (["true", "1", "yes", "si"].includes(normalized)) return true;
    if (["false", "0", "no"].includes(normalized)) return false;
  }
  return fallback;
};

const normalizeProvider = (value) => {
  const normalized = normalizeValue(value);

  if (
    ["mp", "mercadopago", "mercado_pago", "mercado-pago"].includes(normalized)
  ) {
    return "mercadopago";
  }

  if (["pp", "paypal", "pay_pal", "pay-pal"].includes(normalized)) {
    return "paypal";
  }

  if (["both", "ambas", "paypal_mercadopago"].includes(normalized)) {
    return "paypal_mercadopago";
  }

  if (["none", "free", "gratis"].includes(normalized)) {
    return "none";
  }

  return "";
};

const parseProviders = (...values) => {
  const tokens = values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .flatMap((value) =>
      normalizeValue(value)
        .replace(/\+/g, ",")
        .replace(/\//g, ",")
        .replace(/\|/g, ",")
        .replace(/\b(and|y|ambas|both)\b/g, ",")
        .split(",")
    )
    .map((provider) => normalizeProvider(provider))
    .filter(Boolean);

  return [...new Set(tokens)];
};

export const resolveFeatureFlags = (config = {}) => {
  const paymentMode = normalizeProvider(config.payment_mode);
  const paymentProviders = parseProviders(
    config.payment_mode,
    config.payment_providers,
    config.payment_provider,
    config.currency
  );
  const paymentsEnabled = parseBoolean(config.payments_enabled, true);
  const freeEvent = parseBoolean(config.free_event, false);
  const hasRealPaymentProvider = paymentProviders.some(
    (provider) => provider !== "none"
  );

  const isFree =
    paymentMode === "none" ||
    freeEvent ||
    paymentsEnabled === false ||
    (!hasRealPaymentProvider && paymentProviders.includes("none"));

  return {
    IS_FREE: isFree,
    ENABLE_EMISSION: parseBoolean(
      config.enable_emission,
      DEFAULT_ENABLE_EMISSION
    ),
    ENABLE_QUESTIONS: parseBoolean(
      config.enable_questions,
      DEFAULT_ENABLE_QUESTIONS
    ),
    paymentProviders,
    paymentsEnabled,
  };
};

export const persistFeatureFlags = (config = {}) => {
  const flags = resolveFeatureFlags(config);
  const values = {
    public_config_is_free: String(flags.IS_FREE),
    public_config_enable_emission: String(flags.ENABLE_EMISSION),
    public_config_enable_questions: String(flags.ENABLE_QUESTIONS),
    public_config_payment_providers: JSON.stringify(flags.paymentProviders),
  };

  if (typeof sessionStorage !== "undefined") {
    Object.entries(values).forEach(([key, value]) => {
      sessionStorage.setItem(key, value);
    });
  }

  if (typeof localStorage !== "undefined") {
    Object.entries(values).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }
};

const readStoredBoolean = (key, fallback) => {
  const value =
    typeof sessionStorage !== "undefined" && sessionStorage.getItem(key) !== null
      ? sessionStorage.getItem(key)
      : typeof localStorage !== "undefined"
      ? localStorage.getItem(key)
      : null;
  return parseBoolean(value, fallback);
};

export const getRuntimeFeatureFlags = () => ({
  IS_FREE: readStoredBoolean("public_config_is_free", DEFAULT_FREE_EVENT),
  ENABLE_EMISSION: readStoredBoolean(
    "public_config_enable_emission",
    DEFAULT_ENABLE_EMISSION
  ),
  ENABLE_QUESTIONS: readStoredBoolean(
    "public_config_enable_questions",
    DEFAULT_ENABLE_QUESTIONS
  ),
});

export const IS_FREE = DEFAULT_FREE_EVENT;
export const ENABLE_EMISSION = DEFAULT_ENABLE_EMISSION;
export const ENABLE_QUESTIONS = DEFAULT_ENABLE_QUESTIONS;
